import { isTenantOrFacilityAdmin } from "./permissionAccess";

export type SidebarBadgeKey = "MY_LEADS" | "MY_TASKS";

export interface SidebarUnreadCounts {
  total: number;
  MY_LEADS: number;
  MY_TASKS: number;
}

/** Templates that CAR-1212 badges against My Tasks, not Opportunities. */
const MY_TASKS_TEMPLATE_KEYS = new Set([
  "TASK_ASSIGNED",
  "TASK_OUTCOME",
  "TASK_COMMENT",
  "TASK_REMINDER",
  "SLA_ALERT",
]);

/**
 * Prefer the server-provided `sidebarBadgeKey`. If it is missing (older
 * payloads), CAR-1212 still routes task-assignment templates to MY_TASKS.
 */
export function resolveNotificationSidebarBadgeKey(input: {
  templateKey?: string;
  sidebarBadgeKey?: SidebarBadgeKey;
}): SidebarBadgeKey | undefined {
  if (
    input.sidebarBadgeKey === "MY_LEADS" ||
    input.sidebarBadgeKey === "MY_TASKS"
  ) {
    return input.sidebarBadgeKey;
  }
  if (input.templateKey && MY_TASKS_TEMPLATE_KEYS.has(input.templateKey)) {
    return "MY_TASKS";
  }
  return undefined;
}

export function adjustSidebarUnreadCount(
  unread: Pick<SidebarUnreadCounts, "MY_LEADS" | "MY_TASKS">,
  notification: {
    templateKey?: string;
    sidebarBadgeKey?: SidebarBadgeKey;
  },
  delta: number
): void {
  const badgeKey = resolveNotificationSidebarBadgeKey(notification);
  if (badgeKey === "MY_LEADS") {
    unread.MY_LEADS = Math.max(0, (unread.MY_LEADS ?? 0) + delta);
  }
  if (badgeKey === "MY_TASKS") {
    unread.MY_TASKS = Math.max(0, (unread.MY_TASKS ?? 0) + delta);
  }
}

export interface BadgeableNavItem {
  id: string;
  badge?: number;
}

/** Map unread counts onto Opportunities (MY_LEADS) and My Tasks (MY_TASKS). */
export function applySidebarNotificationBadges<T extends BadgeableNavItem>(
  items: T[],
  unread: Pick<SidebarUnreadCounts, "MY_LEADS" | "MY_TASKS">
): Array<T & { badge?: number }> {
  const myLeads = unread.MY_LEADS ?? 0;
  const myTasks = unread.MY_TASKS ?? 0;
  return items.map((item) => {
    if (item.id === "leads" && myLeads > 0) {
      return { ...item, badge: myLeads };
    }
    if (item.id === "spocTasks" && myTasks > 0) {
      return { ...item, badge: myTasks };
    }
    return item;
  });
}

/**
 * My Tasks is the personal inbox (`GET /overview/my-tasks`) and the CAR-1212
 * badge target. Agents with TASKS enabled receive TaskAssigned there; SPOC /
 * UNIT_POC members receive query assignment there.
 */
export function canSeeMyTasksNav(input: {
  roles: string[];
  unitPocActions: string[];
  tasksModuleEnabled: boolean;
}): boolean {
  if (isTenantOrFacilityAdmin(input.roles)) return true;
  if (input.roles.includes("SPOC")) return true;
  if (input.tasksModuleEnabled) return true;
  return input.unitPocActions.some(
    (action) => action === "AGENT" || action === "SPOC" || action === "MANAGE"
  );
}
